<script setup lang="ts">
import { NInput } from 'naive-ui'

// One repeated section of the CV editor (roles, education, skills, …):
// a list of entries with string fields and, optionally, one multi-line
// list field edited one item per line. Reordering and removal are the
// small hand edits the owner makes before approving; anything bigger is a
// regeneration.
import type { EditorEntry, EditorField } from './cvEditorTypes'

const props = defineProps<{
  title: string
  testid: string
  entries: EditorEntry[]
  fields: EditorField[]
  listField?: EditorField
  blank: () => EditorEntry
}>()

const emit = defineEmits<{ 'update:entries': [entries: EditorEntry[]] }>()

function setField(index: number, key: string, value: string): void {
  const next = props.entries.map((e, i) => (i === index ? { ...e, [key]: value } : e))
  emit('update:entries', next)
}

function setList(index: number, key: string, text: string): void {
  const items = text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  const next = props.entries.map((e, i) => (i === index ? { ...e, [key]: items } : e))
  emit('update:entries', next)
}

function listText(entry: EditorEntry, key: string): string {
  const value = entry[key]
  return Array.isArray(value) ? value.join('\n') : ''
}

function stringValue(entry: EditorEntry, key: string): string {
  const value = entry[key]
  return typeof value === 'string' ? value : ''
}

function add(): void {
  emit('update:entries', [...props.entries, props.blank()])
}

function remove(index: number): void {
  emit(
    'update:entries',
    props.entries.filter((_, i) => i !== index),
  )
}

function entriesLabel(count: number): string {
  return `${count} ${count === 1 ? 'entry' : 'entries'}`
}

const BUTTON_BASE =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-border-control px-2.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-8 sm:min-w-8'
const SMALL_BUTTON = `${BUTTON_BASE} text-ink-muted hover:text-ink`
const REMOVE_BUTTON = `${BUTTON_BASE} ml-auto text-status-alert`

function move(index: number, delta: number): void {
  const target = index + delta
  if (target < 0 || target >= props.entries.length) return
  const next = [...props.entries]
  ;[next[index], next[target]] = [next[target], next[index]]
  emit('update:entries', next)
}
</script>

<template>
  <details class="group rounded-xl border border-border-subtle bg-surface-raised" :data-testid="testid">
    <summary
      class="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-xl px-3 py-2.5 group-open:border-b group-open:border-border-divider [&::-webkit-details-marker]:hidden"
    >
      <span class="flex flex-1 flex-col gap-0.5">
        <span class="font-medium text-ink">{{ title }}</span>
        <span class="text-xs text-ink-faint">{{ entriesLabel(entries.length) }}</span>
      </span>
      <span class="text-ink-faint transition-transform group-open:rotate-90" aria-hidden="true">›</span>
    </summary>
    <div class="flex flex-col gap-2.5 p-3">
      <div
        v-for="(entry, i) in entries"
        :key="i"
        class="flex flex-col gap-2 rounded-lg border border-border-divider bg-surface-bg p-2.5"
        :data-testid="`${testid}-${i}`"
      >
        <div class="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
          <label v-for="field in fields" :key="field.key" class="flex flex-col gap-1 text-xs text-ink-muted">
            {{ field.label }}
            <NInput
              :value="stringValue(entry, field.key)"
              :placeholder="field.placeholder"
              size="small"
              :data-testid="`${testid}-${i}-${field.key}`"
              @update:value="(v: string) => setField(i, field.key, v)"
            />
          </label>
        </div>
        <label v-if="listField" class="flex flex-col gap-1 text-xs text-ink-muted">
          {{ listField.label }}
          <NInput
            type="textarea"
            :autosize="{ minRows: 2 }"
            :value="listText(entry, listField.key)"
            :placeholder="listField.placeholder"
            size="small"
            :data-testid="`${testid}-${i}-${listField.key}`"
            @update:value="(v: string) => setList(i, listField!.key, v)"
          />
        </label>
        <div class="flex gap-2">
          <button
            type="button"
            :class="SMALL_BUTTON"
            :disabled="i === 0"
            :aria-label="`Move ${title} entry ${i + 1} up`"
            :data-testid="`${testid}-${i}-up`"
            @click="move(i, -1)"
          >
            ↑
          </button>
          <button
            type="button"
            :class="SMALL_BUTTON"
            :disabled="i === entries.length - 1"
            :aria-label="`Move ${title} entry ${i + 1} down`"
            :data-testid="`${testid}-${i}-down`"
            @click="move(i, 1)"
          >
            ↓
          </button>
          <button
            type="button"
            :class="REMOVE_BUTTON"
            :aria-label="`Remove ${title} entry ${i + 1}`"
            :data-testid="`${testid}-${i}-remove`"
            @click="remove(i)"
          >
            Remove
          </button>
        </div>
      </div>
      <button
        type="button"
        class="min-h-11 self-start rounded-[10px] border border-dashed border-border-control px-3.5 text-[13px] text-ink hover:bg-surface-hover sm:min-h-9"
        :data-testid="`${testid}-add`"
        @click="add"
      >
        + Add {{ title.toLowerCase() }} entry
      </button>
    </div>
  </details>
</template>
